/**
 * Rows-per-page options for list pages that support a card/table view toggle.
 * Table view uses [10,25,50,100]; card view uses [12,24,48,96] since card
 * grids read better in multiples of 3/4/6 columns. Keep this as the single
 * source of truth — do not re-declare these arrays inline per page.
 */
export type TableViewMode = "card" | "table";
export const TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export const CARD_PAGE_SIZE_OPTIONS = [12, 24, 48, 96] as const;

export function getPageSizeOptions(viewMode: TableViewMode): readonly number[] {
  return viewMode === "card" ? CARD_PAGE_SIZE_OPTIONS : TABLE_PAGE_SIZE_OPTIONS;
}

/**
 * Returns a page size guaranteed to be a valid option for `viewMode`. If
 * `pageSize` isn't in that view's option list (e.g. it was set while in the
 * other view, or came from a stale default/localStorage/URL param), snaps to
 * the closest available option instead of leaving a <select> holding a value
 * that isn't one of its own options.
 */
export function resolvePageSizeForViewMode(
  pageSize: number,
  viewMode: TableViewMode,
): number {
  const options = getPageSizeOptions(viewMode);
  if (options.includes(pageSize)) return pageSize;
  if (!pageSize || pageSize <= 0 || !Number.isFinite(pageSize)) return options[0];
  return options.reduce((closest, opt) =>
    Math.abs(opt - pageSize) < Math.abs(closest - pageSize) ? opt : closest,
  options[0]);
}

export function formatVersion(version: number | string | null | undefined): string {
  if (version === null || version === undefined || version === "") return "-";
  const str = String(version);
  return str.startsWith("v") || str.startsWith("V") ? str : `v${str}`;
}

/**
 * Turn a visible UI label into a DOM id, so a widget's id always matches the
 * title the user sees. Lowercased, non-alphanumerics collapsed to hyphens.
 *
 * Examples: "Findings & POA&M Status" → "findings-poam-status",
 *           "SSP Coverage" → "ssp-coverage".
 */
export function labelToDomId(label: string, suffix = ""): string {
  const slug = label
    .toLowerCase()
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return suffix ? `${slug}-${suffix}` : slug;
}

/** Max length of an SSP short code (matches the backend's cap). */
export const SSP_SHORT_CODE_MAX = 10;

/**
 * Normalize an SSP short code the way the backend stores it: uppercased, only
 * ASCII letters/digits (spaces, hyphens, symbols, accented/unicode letters all
 * removed), capped at SSP_SHORT_CODE_MAX chars. Safe for any input —
 * null/undefined/non-string yield "".
 *
 * Examples: "  pay-1 " → "PAY1", "café" → "CAF", "a_b c!" → "ABC".
 */
export function sanitizeSspShortCode(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, SSP_SHORT_CODE_MAX);
}

/**
 * Safely formats a date-like input. Returns `fallback` (default "-") for
 * null, undefined, empty strings, or unparseable dates — never "Invalid Date".
 * Locale defaults to "en-GB" (DD/MM/YYYY) to match existing card conventions.
 */
export function safeFormatDate(
  value: string | number | Date | null | undefined,
  locale: string = "en-GB",
  fallback: string = "-",
): string {
  if (value === null || value === undefined || value === "") return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return fallback;
  try {
    return d.toLocaleDateString(locale);
  } catch {
    return fallback;
  }
}

export function todayISO(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * Formats a date-like input as `DD/MM/YYYY, hh:mm AM/PM` (e.g. "25/02/2026, 08:52 PM").
 * Returns `fallback` (default "-") for null, undefined, empty, or unparseable dates.
 */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  fallback: string = "-",
): string {
  if (value === null || value === undefined || value === "") return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return fallback;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const rawHours = d.getHours();
  const ampm = rawHours >= 12 ? "PM" : "AM";
  const hour12 = String(rawHours % 12 || 12).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year}, ${hour12}:${minutes} ${ampm}`;
}

const MAX_JSON_UNWRAPS = 3;

const MAX_EVIDENCE_DEPTH = 12;

function parseJsonDeep(text: string): unknown {
  let current: unknown = text;
  for (let pass = 0; pass < MAX_JSON_UNWRAPS; pass += 1) {
    if (typeof current !== "string") return current;
    const trimmed = current.trim();
    const first = trimmed[0];
    if (first !== "{" && first !== "[" && first !== '"') {
      return pass === 0 ? undefined : current;
    }
    try {
      current = JSON.parse(trimmed);
    } catch {
      return pass === 0 ? undefined : current;
    }
  }
  return current;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function decodeValue(value: unknown, depth: number): unknown {
  if (depth >= MAX_EVIDENCE_DEPTH) return value;
  if (typeof value === "string") {
    const parsed = parseJsonDeep(value);
    if (parsed !== undefined && typeof parsed === "object" && parsed !== null) {
      return decodeValue(parsed, depth + 1);
    }
    return value;
  }
  if (Array.isArray(value)) return value.map((entry) => decodeValue(entry, depth + 1));
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      out[key] = decodeValue(entry, depth + 1);
    }
    return out;
  }
  return value;
}

export function decodeEvidence(raw: unknown): unknown {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw === "object") return decodeValue(raw, 0);
  if (typeof raw !== "string") return raw;
  if (raw.trim() === "") return undefined;
  const parsed = parseJsonDeep(raw);
  if (parsed === undefined) return undefined;
  return decodeValue(parsed, 0);
}

export function formatEvidenceJson(raw: unknown): string | null {
  const decoded = decodeEvidence(raw);
  if (decoded === undefined) return null;
  try {
    return JSON.stringify(decoded, null, 2);
  } catch {
    return null;
  }
}

